import { Banner } from "@/components/flexkit/Banner";
import { Button } from "@/components/flexkit/Button";
import { CopyableInput } from "@/components/podkit/forms/CopyableInput";
import { Input } from "@/components/podkit/forms/Input";
import { InputField } from "@/components/podkit/forms/InputField";
import {
    DialogBody,
    DialogClose,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { ExternalLink } from "@/components/podkit/typography/ExternalLink";
import { Text } from "@/components/podkit/typography/Text";
import { useNewInAppChatMessage } from "@/hooks/use-boot-in-app-chat";
import { useGetOrCreateDomainVerification, useVerifyDomain } from "@/queries/organization-queries";
import { formatError } from "@/utils/errors";
import { DomainVerificationState } from "gitpod-next-api/gitpod/v1/organization_pb";
import { useCallback, useId, type FC } from "react";

type DomainVerificationProps = {
    organizationId: string;
    domain: string;
    onCancel: () => void;
};

export const DomainVerification: FC<DomainVerificationProps> = ({ onCancel, domain }) => {
    const { toast } = useToast();
    const showNewInAppChatMessage = useNewInAppChatMessage();

    // domain verification state
    const { domainVerification, error } = useGetOrCreateDomainVerification(domain);
    const token = domainVerification?.verificationToken ?? "";

    const verifyDomain = useVerifyDomain();
    const handleVerifyDomain = useCallback(async () => {
        if (!domainVerification) {
            return;
        }
        try {
            const result = await verifyDomain.mutateAsync(domainVerification);
            const title =
                result?.state === DomainVerificationState.VERIFIED
                    ? `${domain} is verified.`
                    : `${domain} is not yet verified. Please try again later.`;

            toast({
                title,
            });
            onCancel();
        } catch (error) {
            toast({
                title: "Failed to verify domain.",
                description: formatError(error),
            });
        }
    }, [domain, domainVerification, toast, verifyDomain, onCancel]);

    // ids for the input fields
    const inputDomainID = useId();
    const inputTokenKeyID = useId();
    const inputTokenValueID = useId();

    const handleReachOut = useCallback(() => {
        showNewInAppChatMessage(
            `Hi! I need to verify my "${domain}" domain for SSO but cannot create any DNS TXT record. Can you help me?`,
        );
    }, [showNewInAppChatMessage, domain]);

    return (
        <div>
            <DialogHeader className="flex flex-col gap-2">
                <DialogTitle>Verify your domain</DialogTitle>
                <DialogDescription>
                    Configure OpenID Connect (OIDC) single sign-on with your identity provider.
                </DialogDescription>
            </DialogHeader>
            <DialogBody className="flex w-full py-6">
                <div className="flex w-full flex-col gap-4">
                    <Text>
                        We need to ensure you own the domain. To do that, you&apos;ll need to create a new DNS TXT
                        record for your domain.{" "}
                        <ExternalLink href="https://ona.com/docs/ona/sso/overview">Learn more</ExternalLink>.
                    </Text>
                    {error && (
                        <Banner
                            variant="danger"
                            text={
                                <>
                                    <div className="font-bold">Failed to fetch the verification data.</div>
                                    <div className="text-sm">{formatError(error)}</div>
                                </>
                            }
                        />
                    )}
                    <InputField
                        label="Email domain"
                        id={inputDomainID}
                        hint="This is the work email domain that people will use to sign in."
                    >
                        <Input
                            data-testid="domain-input"
                            id={inputDomainID}
                            type="text"
                            name="domain"
                            value={domain}
                            disabled
                        />
                    </InputField>
                    <InputField
                        label="TXT record name"
                        id={inputTokenKeyID}
                        hint={"This is the name of the TXT record."}
                    >
                        <CopyableInput
                            disabled={!token}
                            data-testid="token-name-input"
                            data-tracking-id="txt-record-name-domain-verification"
                            id={inputTokenKeyID}
                            type="text"
                            name="token-name"
                            value={domain}
                            readOnly={true}
                        />
                    </InputField>
                    <InputField
                        label="TXT record value"
                        id={inputTokenValueID}
                        hint={"This is the value of the TXT record."}
                    >
                        <CopyableInput
                            disabled={!token}
                            data-testid="token-value-input"
                            data-tracking-id="txt-record-value-domain-verification"
                            id={inputTokenValueID}
                            type="text"
                            name="token-value"
                            value={token}
                            readOnly={true}
                        />
                    </InputField>
                    <Text>
                        If you cannot use TXT record validation,{" "}
                        <Button
                            variant="link"
                            className="p-0 text-base text-content-link"
                            onClick={handleReachOut}
                            data-tracking-id="reach-out-to-us-domain-verification"
                        >
                            please reach out to us.
                        </Button>
                    </Text>
                </div>
            </DialogBody>
            <DialogFooter>
                <DialogClose asChild>
                    <Button variant="secondary" onClick={onCancel} data-tracking-id="cancel-domain-verification">
                        Cancel
                    </Button>
                </DialogClose>
                <Button
                    autoFocus={true}
                    variant="primary"
                    disabled={!domainVerification}
                    onClick={handleVerifyDomain}
                    data-tracking-id="verify-domain-verification"
                >
                    Verify
                </Button>
            </DialogFooter>
        </div>
    );
};
